import i18n from 'i18n';
import { CURRENCY_MAP, CurrencyType } from './currency';
import LOCALS from 'commons/locals';
import { LANGUAGE_MAP, LanguageType } from './language';
import { STOCK_PLACE_MAP, StockPlace } from './stock-place';

const RAW_SHOP_MAP = {
  GINZA: 1, // 银座店
  HONGKONG: 2, // 香港店
  SINGAPORE: 3, // 新加坡 ASU
  SINGAPORE_ASU_GST: 13, // 新加坡 ASU GST
  SINGAPORE_GX: 14, // 新加坡 GinzaXiaoma
  SHANG_HAI: 11, // 上海店
  UMEDA: 12, // 梅田店
  DUBAI: 15, // 迪拜店
  FRANCE: 16, // 法国店
  NEW_YORK: 17, // 美国纽约店
} as const;

// 判断 shopId 是否有效， 类型保护
export const isShopId = (shopId: number): shopId is ShopId => {
  return Object.values(RAW_SHOP_MAP).includes(shopId as ShopId);
};

// 店铺配置
const RAW_SHOP_CONFIG_MAP: Record<
  ShopId,
  {
    currency: CurrencyType;
    countryCode: string;
    label: string;
    companyName: string;
    language: LanguageType;
    stockPlace: StockPlace;
  }
> = {
  [RAW_SHOP_MAP.GINZA]: {
    currency: CURRENCY_MAP.JPY,
    countryCode: '81',
    label: i18n.t(LOCALS.ginza_shop),
    companyName: 'ASU BRAND 株式会社',
    language: LANGUAGE_MAP.JA,
    stockPlace: STOCK_PLACE_MAP.JAPAN,
  },
  [RAW_SHOP_MAP.HONGKONG]: {
    currency: CURRENCY_MAP.HKD,
    countryCode: '852',
    label: i18n.t(LOCALS.hongkong_shop),
    companyName: 'ASU Company Limited',
    language: LANGUAGE_MAP.EN,
    stockPlace: STOCK_PLACE_MAP.HONGKONG,
  },
  [RAW_SHOP_MAP.SINGAPORE]: {
    currency: CURRENCY_MAP.SGD,
    countryCode: '65',
    label: i18n.t(LOCALS.singapore_shop),
    companyName: 'ASU Company',
    language: LANGUAGE_MAP.EN,
    stockPlace: STOCK_PLACE_MAP.SINGAPORE_ASU,
  },
  [RAW_SHOP_MAP.SINGAPORE_ASU_GST]: {
    currency: CURRENCY_MAP.SGD,
    countryCode: '65',
    label: i18n.t(LOCALS.singapore_shop_asu_gst),
    companyName: 'ASU Company',
    language: LANGUAGE_MAP.EN,
    stockPlace: STOCK_PLACE_MAP.SINGAPORE_ASU_GST,
  },
  [RAW_SHOP_MAP.SINGAPORE_GX]: {
    currency: CURRENCY_MAP.SGD,
    countryCode: '65',
    label: i18n.t(LOCALS.kkIwEnEVml),
    companyName: 'GINZAXIAOMA',
    language: LANGUAGE_MAP.EN,
    stockPlace: STOCK_PLACE_MAP.SINGAPORE_GX,
  },
  [RAW_SHOP_MAP.SHANG_HAI]: {
    currency: CURRENCY_MAP.CNY,
    countryCode: '86',
    label: i18n.t(LOCALS.shanghai_store),
    companyName: 'XIAOMA 上海',
    language: LANGUAGE_MAP.ZH_CN,
    stockPlace: STOCK_PLACE_MAP.SHANG_HAI,
  },
  [RAW_SHOP_MAP.UMEDA]: {
    currency: CURRENCY_MAP.JPY,
    countryCode: '81',
    label: i18n.t(LOCALS.umeda_store),
    companyName: 'ASU BRAND 株式会社',
    language: LANGUAGE_MAP.JA,
    stockPlace: STOCK_PLACE_MAP.UMEDA,
  },
  [RAW_SHOP_MAP.DUBAI]: {
    currency: CURRENCY_MAP.AED,
    countryCode: '971',
    label: i18n.t(LOCALS.dubai_store),
    companyName: 'ASU Company Limited',
    language: LANGUAGE_MAP.EN,
    stockPlace: STOCK_PLACE_MAP.DUBAI,
  },
  [RAW_SHOP_MAP.FRANCE]: {
    currency: CURRENCY_MAP.EUR,
    countryCode: '33',
    label: i18n.t(LOCALS.france_store),
    companyName: 'XIAOMA PARIS',
    // 因为法文没有对应的语言包, 所以单独处理为英文, 特定地方需要手动渲染法语
    language: LANGUAGE_MAP.EN,
    stockPlace: STOCK_PLACE_MAP.FRANCE,
  },
  [RAW_SHOP_MAP.NEW_YORK]: {
    currency: CURRENCY_MAP.USD,
    countryCode: '1',
    label: i18n.t(LOCALS.new_york_store),
    companyName: 'XIAOMA NEW YORK',
    language: LANGUAGE_MAP.EN,
    stockPlace: STOCK_PLACE_MAP.NEW_YORK,
  },
} as const;

const SHOP_CONFIG_MAP = new Proxy(RAW_SHOP_CONFIG_MAP, {
  ownKeys() {
    throw new Error(
      '❌ 禁止遍历 SHOP_CONFIG_MAP！请直接通过属性精准获取值，如 SHOP_CONFIG_MAP.GINZA',
    );
  },
});

export const SHOP_MAP = new Proxy(RAW_SHOP_MAP, {
  ownKeys() {
    throw new Error(
      '❌ 禁止遍历 SHOP_MAP！请直接通过属性精准获取值，如 SHOP_MAP.GINZA',
    );
  },
});

type ShopId = (typeof RAW_SHOP_MAP)[keyof typeof RAW_SHOP_MAP];

export const getShopConfig = (shopId: number) => {
  if (!isShopId(shopId)) {
    throw new Error(`Invalid shopId: ${shopId}`);
  }
  return SHOP_CONFIG_MAP[shopId];
};

export const SHOP_OPTION_LIST = Object.values(RAW_SHOP_MAP).map((shopId) => {
  return {
    value: shopId,
    label: getShopConfig(shopId).label,
  };
});
